import React, { forwardRef, ReactNode, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ImageSourcePropType, ScrollView, useWindowDimensions } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import boxShadowRectangleDesktopImage from '../../assets/wellnessCheckIn/BoxShadowRectangleDesktop.png'
import boxShadowRectangleMobileImage from '../../assets/wellnessCheckIn/BoxShadowRectangleMobile.png'
import { Image } from '../../atoms/image/Image'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type WellnessCheckInResultsPreviewOverlayProps = {
  children: ReactNode
}

const OverlayContainer = styled(ScrollView)({
  position: 'fixed',
  top: '0px',
  right: '0px',
  bottom: '0px',
  left: '0px',
  background: 'transparent',
})

const OverlayImageContainer = styled(Image)<{ theme: ThemeType }>(({ theme }) => ({
  height: theme.breakpoints.isMobileSized ? '180px' : '208px',
  width: '100%',
  top: theme.breakpoints.isMobileSized ? '-140px' : '-156px',
  position: 'absolute',
}))

const ModalContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  minHeight: '860px',
}))

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  borderTopLeftRadius: theme.breakpoints.isMobileSized ? '24px' : '40px',
  borderTopRightRadius: theme.breakpoints.isMobileSized ? '24px' : '40px',
  flex: 1,
}))

const TransparentBackground = styled.View<{ windowHeight: number; transparentHeight: string }>(
  ({ windowHeight, transparentHeight }) => ({
    height: transparentHeight,
    ...(windowHeight > 1000 && {
      height: '700px',
    }),
    ...(windowHeight > 2000 && {
      height: `${0.3 * windowHeight}px`,
    }),
  }),
)

export const WellnessCheckInResultsPreviewOverlay = forwardRef(
  ({ children }: WellnessCheckInResultsPreviewOverlayProps, _) => {
    const { height } = useWindowDimensions()
    const originalDeviceHeightRef = useRef<number | null>(null)
    const { breakpoints } = useTheme()

    // Since overlay could be based on device height, the input keyboard decreases the device viewport height
    // which causes a jump when email input is focused. The ref will keep track of the original device height.
    if (originalDeviceHeightRef.current === null) {
      originalDeviceHeightRef.current = Math.min(550, 0.7 * height)
    }

    // Show the smaller value between 550px and 70% of the view port height
    const transparentContainerHeight = `${originalDeviceHeightRef.current || 0}px`
    const renderOverlay = () => (
      <OverlayContainer testID={tID('WellnessCheckInResultsPreviewOverlayContainer')}>
        <TransparentBackground windowHeight={height} transparentHeight={transparentContainerHeight} />
        <ModalContainer>
          <OverlayImageContainer
            source={
              breakpoints.isMobileSized
                ? (boxShadowRectangleMobileImage as ImageSourcePropType)
                : (boxShadowRectangleDesktopImage as ImageSourcePropType)
            }
            contentFit='cover'
            accessibilityIgnoresInvertColors
            testID={tID('WellnessCheckInResultsBoxShadow')}
          />
          <ContentContainer>{children}</ContentContainer>
        </ModalContainer>
      </OverlayContainer>
    )

    return IS_WEB ? createPortal(renderOverlay(), document.body) : <></>
  },
)
